import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate, normalizeRows, orDash } from "./AccountsShared";
import { downloadAccountsInvoicePdf } from "../../utils/pdfDocs";
import "./AccountsInvoices.css";

const PAYMENT_TYPES = ["CREDIT", "CASH", "CHEQUE", "ONLINE"];
const INVOICE_ACCESS_PERMS = [
  "finance:invoice:create",
  "finance:invoice:approve",
  "finance:expense:create",
  "finance:expense:approve",
  "finance:vendor-bill:upload",
  "finance:vendor-bill:approve",
  "finance:payments:update",
  "finance:payments:approve",
  "finance:payments:release",
  "finance:gst:view",
  "finance:tds:view",
  "finance:reports:view",
  "finance:audit-logs:view",
  "salary:view",
  "salary:process",
  "salary:approve",
];
const createBankEntry = (overrides = {}) => ({
  id: overrides.id || `bank-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  label: overrides.label || "",
  bankName: overrides.bankName || "",
  accountName: overrides.accountName || "",
  accountNumber: overrides.accountNumber || "",
  ifsc: overrides.ifsc || "",
  branch: overrides.branch || "",
  upiId: overrides.upiId || "",
  isDefault: !!overrides.isDefault,
});
const createInvoiceDefaults = () => ({
  termsTitle: "Terms & Conditions",
  notes: "",
  documentRemarks: "",
  paymentType: "CREDIT",
  seriesByType: {
    sales: { invoiceSeries: "TAX INVOICE", invoicePrefix: "SAL", invoiceSuffix: "" },
    purchase: { invoiceSeries: "TAX INVOICE", invoicePrefix: "PUR", invoiceSuffix: "" },
  },
  defaultBankId: "",
  banks: [],
});
const getDefaultBank = (defaults) => {
  const banks = Array.isArray(defaults?.banks) ? defaults.banks : [];
  return (
    banks.find((bank) => String(bank.id) === String(defaults?.defaultBankId || "")) ||
    banks.find((bank) => bank.isDefault) ||
    banks[0] ||
    null
  );
};
const createInvoiceForm = (defaults = createInvoiceDefaults(), invoiceType = "sales") => {
  const resolvedDefaults = {
    ...createInvoiceDefaults(),
    ...(defaults || {}),
    banks: Array.isArray(defaults?.banks) ? defaults.banks.map((bank) => createBankEntry(bank)) : [],
  };
  const selectedBank = getDefaultBank(resolvedDefaults);
  const typeKey = invoiceType === "purchase" ? "purchase" : "sales";
  const typeSeries = resolvedDefaults?.seriesByType?.[typeKey] || createInvoiceDefaults().seriesByType[typeKey];
  return {
    invoiceNo: "",
    invoiceSeries: typeSeries.invoiceSeries || "TAX INVOICE",
    invoiceType: typeKey,
    vendorId: "",
    invoicePrefix: typeSeries.invoicePrefix || "",
    invoiceSuffix: typeSeries.invoiceSuffix || "",
    dueDate: todayInputValue(),
    invoiceDate: todayInputValue(),
    reverseCharge: "No",
    shipTo: "--",
    placeOfSupply: "",
    driverNo: "",
    poNo: "",
    poDate: "",
    lrNo: "",
    eWayNo: "",
    deliveryMode: "",
    transporterName: "",
    vehicleNumber: "",
    driverName: "",
    driverMobile: "",
    dispatchDate: todayInputValue(),
    expectedDeliveryDate: "",
    deliveryAddress: "",
    currentLocation: "",
    deliveryInstructions: "",
    termsTitle: resolvedDefaults.termsTitle || "Terms & Conditions",
    notes: resolvedDefaults.notes || "",
    documentRemarks: resolvedDefaults.documentRemarks || "",
    bankId: selectedBank?.id || "",
    bankName: selectedBank?.bankName || "",
    paymentType: resolvedDefaults.paymentType || "CREDIT",
    items: [emptyItem()],
  };
};

const emptyItem = () => ({
  productId: "",
  description: "",
  note: "",
  hsnSac: "",
  unit: "",
  qty: 1,
  rate: 0,
  discount: 0,
  taxPercent: 0,
});
const findRateTier = (product, quantity) => {
  const qty = Number(quantity || 0);
  const tiers = Array.isArray(product?.pricingTiers) ? product.pricingTiers : [];
  if (!tiers.length) return null;
  return (
    tiers.find((tier) => {
      const min = Number(tier?.minQty ?? 0);
      const max = tier?.maxQty === null || tier?.maxQty === undefined ? null : Number(tier.maxQty);
      if (!Number.isFinite(min)) return false;
      if (qty < min) return false;
      if (max !== null && Number.isFinite(max) && qty > max) return false;
      return true;
    }) ||
    tiers[tiers.length - 1] ||
    null
  );
};
const applyProductPricing = (item, product, quantityOverride) => {
  if (!product) return item;
  const qty = quantityOverride !== undefined ? Number(quantityOverride || 0) : Number(item.qty || 0);
  const tier = findRateTier(product, qty);
  return {
    ...item,
    productId: product._id,
    description: product.name || item.description || "",
    unit: product.unit || item.unit || "",
    qty: quantityOverride !== undefined ? quantityOverride : item.qty,
    rate: Number(tier?.rate || 0),
    taxPercent: Number(product.gstPercent || 0),
  };
};
const hasExactSelectedProduct = (item, products) => {
  const selectedProduct = (products || []).find((product) => String(product._id) === String(item?.productId || ""));
  if (!selectedProduct) return false;
  return String(selectedProduct.name || "").trim().toLowerCase() === String(item?.description || "").trim().toLowerCase();
};
const calcTotals = (items = []) => {
  const normalized = (items || []).map((it) => {
    const qty = Number(it.qty || 0);
    const rate = Number(it.rate || 0);
    const discount = Number(it.discount || 0);
    const taxPercent = Number(it.taxPercent || 0);
    const grossAmount = qty * rate;
    const lineSubtotal = Math.max(grossAmount - discount, 0);
    const lineTax = (lineSubtotal * taxPercent) / 100;
    return { ...it, discount, grossAmount, lineSubtotal, lineTax, lineTotal: lineSubtotal + lineTax };
  });
  const subtotal = normalized.reduce((acc, it) => acc + it.lineSubtotal, 0);
  const taxAmount = normalized.reduce((acc, it) => acc + it.lineTax, 0);
  const totalAmount = subtotal + taxAmount;
  return { items: normalized, subtotal, taxAmount, totalAmount };
};
const normalizePostalValue = (value) => String(value || "").trim();
const buildInvoiceNumber = (prefix, invoiceNo, suffix) =>
  [prefix, invoiceNo, suffix]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join("");
const todayInputValue = () => new Date().toISOString().slice(0, 10);

export const AccountsInvoices = () => {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showVendorCreate, setShowVendorCreate] = useState(false);
  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailSubmitting, setDetailSubmitting] = useState(false);
  const [detailForm, setDetailForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [vendorSubmitting, setVendorSubmitting] = useState(false);
  const [vendorLookupLoading, setVendorLookupLoading] = useState(false);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const [createdVendorPassword, setCreatedVendorPassword] = useState("");
  const [vendorLookupMessage, setVendorLookupMessage] = useState("");
  const [invoiceDefaults, setInvoiceDefaults] = useState(createInvoiceDefaults());
  const [defaultsForm, setDefaultsForm] = useState(createInvoiceDefaults());
  const [activeInvoiceType, setActiveInvoiceType] = useState("sales");
  const [vendorPincodeMeta, setVendorPincodeMeta] = useState({
    loading: false,
    error: "",
    localities: [],
  });
  const [form, setForm] = useState(createInvoiceForm(createInvoiceDefaults(), "sales"));
  const [vendorForm, setVendorForm] = useState({
    companyName: "",
    contactName: "",
    mobile: "",
    email: "",
    gstNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    vendorType: "Distributor",
    turnover: "",
    contactDesignation: "",
    whatsappNumber: "",
    panNumber: "",
    notes: "",
  });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/invoices");
      setRows(normalizeRows(res.data));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load invoices");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchInvoiceDefaults = async () => {
    try {
      setDefaultsLoading(true);
      const res = await API.get("/employee/accounts/invoices/defaults");
      const nextDefaults = {
        ...createInvoiceDefaults(),
        ...(res?.data || {}),
        seriesByType: {
          ...createInvoiceDefaults().seriesByType,
          ...(res?.data?.seriesByType || {}),
          sales: {
            ...createInvoiceDefaults().seriesByType.sales,
            ...(res?.data?.seriesByType?.sales || {}),
          },
          purchase: {
            ...createInvoiceDefaults().seriesByType.purchase,
            ...(res?.data?.seriesByType?.purchase || {}),
          },
        },
        banks: Array.isArray(res?.data?.banks) ? res.data.banks.map((bank) => createBankEntry(bank)) : [],
      };
      setInvoiceDefaults(nextDefaults);
      setDefaultsForm(nextDefaults);
    } catch {
      const fallbackDefaults = createInvoiceDefaults();
      setInvoiceDefaults(fallbackDefaults);
      setDefaultsForm(fallbackDefaults);
    } finally {
      setDefaultsLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await API.get("/employee/accounts/invoices/vendors");
      setVendors(normalizeRows(res.data));
    } catch {
      setVendors([]);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchInvoiceDefaults();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await API.get("/employee/products");
        setProducts(Array.isArray(res.data) ? res.data : []);
      } catch {
        setProducts([]);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
  }, []);

  const canCreate = INVOICE_ACCESS_PERMS.some((p) => perms.includes(p));
  const canApprove = ["finance:invoice:approve"].some((p) => perms.includes(p));
  const createTotals = useMemo(() => calcTotals(form.items), [form.items]);
  const selectedVendor = useMemo(
    () => vendors.find((v) => String(v._id) === String(form.vendorId)),
    [vendors, form.vendorId]
  );
  const selectedBank = useMemo(
    () =>
      (invoiceDefaults?.banks || []).find((bank) => String(bank.id) === String(form.bankId || "")) ||
      (form.bankName ? (invoiceDefaults?.banks || []).find((bank) => bank.bankName === form.bankName) : null) ||
      null,
    [invoiceDefaults, form.bankId, form.bankName]
  );
  const createGrandTotal = createTotals.totalAmount;
  const createSubtotal = createTotals.subtotal;
  const createTaxAmount = createTotals.taxAmount;
  const createAdditionalCharge = 0;
  const createDiscountAmount = createTotals.items.reduce((acc, item) => acc + Number(item.discount || 0), 0);
  const productOptions = useMemo(
    () =>
      (products || []).map((product) => ({
        ...product,
        searchKey: String(product.name || "").trim().toLowerCase(),
      })),
    [products]
  );

  const openCreate = (invoiceType = activeInvoiceType) => {
    setErr("");
    setForm(createInvoiceForm(invoiceDefaults, invoiceType));
    setShowCreate(true);
  };

  const openDefaultsModal = () => {
    setDefaultsForm({
      ...createInvoiceDefaults(),
      ...invoiceDefaults,
      seriesByType: {
        ...createInvoiceDefaults().seriesByType,
        ...(invoiceDefaults?.seriesByType || {}),
        sales: {
          ...createInvoiceDefaults().seriesByType.sales,
          ...(invoiceDefaults?.seriesByType?.sales || {}),
        },
        purchase: {
          ...createInvoiceDefaults().seriesByType.purchase,
          ...(invoiceDefaults?.seriesByType?.purchase || {}),
        },
      },
      banks: Array.isArray(invoiceDefaults?.banks) ? invoiceDefaults.banks.map((bank) => createBankEntry(bank)) : [],
    });
    setShowDefaultsModal(true);
  };

  const openVendorCreate = () => {
    setCreatedVendorPassword("");
    setVendorLookupMessage("");
    setVendorForm({
      companyName: "",
      contactName: "",
      mobile: "",
      email: "",
      gstNumber: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pincode: "",
      vendorType: "Distributor",
      turnover: "",
      contactDesignation: "",
      whatsappNumber: "",
      panNumber: "",
      notes: "",
    });
    setVendorPincodeMeta({ loading: false, error: "", localities: [] });
    setShowVendorCreate(true);
  };

  const runVendorGstLookup = async () => {
    const gstNumber = String(vendorForm.gstNumber || "").trim().toUpperCase();
    if (!gstNumber) {
      setErr("GST number enter kijiye.");
      return;
    }
    try {
      setVendorLookupLoading(true);
      setVendorLookupMessage("");
      setErr("");
      const res = await API.get("/employee/accounts/gst-lookup", { params: { gstNumber } });
      const data = res.data || {};
      const companyName = data.companyName || data.tradeName || data.legalName || "";
      const address = data.address || "";
      setVendorForm((prev) => ({
        ...prev,
        gstNumber: data.gstNumber || gstNumber,
        companyName: companyName || prev.companyName,
        contactName: data.contactName || prev.contactName || companyName,
        mobile: data.mobile || prev.mobile,
        email: data.email || prev.email,
        panNumber: data.panNumber || prev.panNumber,
        addressLine1: address || prev.addressLine1,
        city: data.city || prev.city,
        state: data.state || prev.state,
        pincode: data.pincode || prev.pincode,
        turnover: data.gstTurnoverSlab || data.gstTurnover || prev.turnover,
        whatsappNumber: data.mobile || prev.whatsappNumber,
      }));
      setVendorLookupMessage(
        data.provider === "sandbox" || data.provider === "rapidapi"
          ? "GST portal details loaded."
          : data.providerError
            ? `GST fallback used: ${data.providerError}`
            : data.providerConfigured === false
              ? "GST API key configured nahi hai. GSTIN se PAN/state/entity details decode hue."
              : "GST details decoded."
      );
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "GST lookup failed");
    } finally {
      setVendorLookupLoading(false);
    }
  };

  useEffect(() => {
    if (!showVendorCreate) return undefined;
    const pincode = String(vendorForm.pincode || "").trim();
    if (!/^[0-9]{6}$/.test(pincode)) {
      setVendorPincodeMeta({ loading: false, error: "", localities: [] });
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setVendorPincodeMeta((prev) => ({ ...prev, loading: true, error: "" }));
        const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        const result = Array.isArray(data) ? data[0] : null;
        const offices = Array.isArray(result?.PostOffice) ? result.PostOffice : [];
        if (!offices.length) {
          setVendorPincodeMeta({ loading: false, error: "Pincode se city/state nahi mila", localities: [] });
          return;
        }

        const primaryOffice = offices[0] || {};
        const city = normalizePostalValue(primaryOffice.District || primaryOffice.Block || primaryOffice.Name);
        const state = normalizePostalValue(primaryOffice.State);
        const localities = Array.from(
          new Set(
            offices
              .map((office) => normalizePostalValue(office.Name))
              .filter(Boolean)
          )
        );

        setVendorForm((prev) => ({
          ...prev,
          city: city || prev.city,
          state: state || prev.state,
          addressLine2:
            prev.addressLine2 ||
            normalizePostalValue(primaryOffice.Block || primaryOffice.Name),
        }));
        setVendorPincodeMeta({ loading: false, error: "", localities });
      } catch (error) {
        if (error?.name === "AbortError") return;
        setVendorPincodeMeta({ loading: false, error: "Pincode lookup failed", localities: [] });
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [showVendorCreate, vendorForm.pincode]);

  const buildInvoicePayload = () => {
    const vendor = selectedVendor;
    const validItems = createTotals.items.filter((i) => String(i.description || "").trim() && Number(i.qty) > 0);
    const invoiceNo = buildInvoiceNumber(form.invoicePrefix, form.invoiceNo, form.invoiceSuffix);
    if (!invoiceNo.trim()) return { error: "Invoice No is required" };
    if (!vendor) return { error: "Please select a vendor" };
    if (!validItems.length) return { error: "Add at least one valid item" };
    if ((form.invoiceType || "sales") === "purchase" && validItems.some((item) => !item.productId)) {
      return { error: "Purchase invoice me har item admin product catalog se select karna hoga" };
    }
    if (form.deliveryMode) {
      if (!String(form.driverName || "").trim()) return { error: "Delivery select karne par driver name required hai" };
      if (!String(form.driverMobile || "").trim()) return { error: "Delivery select karne par driver mobile required hai" };
      if (!String(form.vehicleNumber || "").trim()) return { error: "Delivery select karne par vehicle number required hai" };
      if (!String(form.deliveryAddress || "").trim()) return { error: "Delivery select karne par delivery address required hai" };
    }
    const notes = [
      form.termsTitle ? `${form.termsTitle}` : "",
      form.notes || "",
      form.deliveryInstructions ? `Delivery instructions: ${form.deliveryInstructions}` : "",
      form.documentRemarks ? `Document note: ${form.documentRemarks}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    return {
      payload: {
        invoiceNo,
        invoiceType: form.invoiceType || "sales",
        vendorId: vendor._id,
        vendor: vendor.companyName || vendor.name || vendor.fullName,
        dueDate: form.dueDate || undefined,
        invoiceDate: form.invoiceDate || undefined,
        referenceNo: form.eWayNo || form.lrNo || form.poNo || "",
        notes,
        dispatchDetails: {
          trackingId: form.eWayNo || "",
          driverName: form.driverName || vendor.contactName || vendor.fullName || vendor.name || "",
          driverMobile: form.driverMobile || form.driverNo || vendor.contactMobile || vendor.mobile || "",
          transporterName: form.transporterName || form.deliveryMode || "",
          purchaseOrderNo: form.poNo || "",
          vehicleNumber: form.vehicleNumber || form.lrNo || "",
          dispatchDate: form.dispatchDate || undefined,
          expectedDeliveryDate: form.expectedDeliveryDate || form.poDate || undefined,
          locationAddress: form.deliveryAddress || vendor.address || form.placeOfSupply || "",
          currentLocation: form.currentLocation || form.deliveryAddress || vendor.address || form.placeOfSupply || "",
          note: form.deliveryInstructions || "",
        },
        items: validItems,
        amount: createTotals.totalAmount,
        invoiceSeries: form.invoiceSeries || "",
        invoicePrefix: form.invoicePrefix || "",
        invoiceSuffix: form.invoiceSuffix || "",
        termsTitle: form.termsTitle || "",
        documentRemarks: form.documentRemarks || "",
        paymentType: form.paymentType || "CREDIT",
        bankId: form.bankId || "",
        bankName: selectedBank?.bankName || form.bankName || "",
        bankDetails: selectedBank || null,
      },
    };
  };

  const submitCreate = async (mode = "draft") => {
    const { payload, error } = buildInvoicePayload();
    if (error) return setErr(error);
    try {
      setErr("");
      setSubmitting(true);
      const created = await API.post("/employee/accounts/invoices", payload);
      if (mode === "approve") {
        const id = created?.data?._id;
        if (id) {
          await API.patch(`/employee/accounts/invoices/${id}/status`, { status: "approved" });
        }
      }
      setShowCreate(false);
      fetchRows();
      fetchVendors();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const setDefaultBankId = (bankId) => {
    setDefaultsForm((prev) => ({
      ...prev,
      defaultBankId: bankId,
      banks: (prev.banks || []).map((bank) => ({ ...bank, isDefault: String(bank.id) === String(bankId) })),
    }));
  };

  const addDefaultsBank = () => {
    const nextBank = createBankEntry();
    setDefaultsForm((prev) => {
      const hasBanks = Array.isArray(prev.banks) && prev.banks.length > 0;
      const bankId = hasBanks ? prev.defaultBankId : nextBank.id;
      return {
        ...prev,
        defaultBankId: bankId,
        banks: [...(prev.banks || []), { ...nextBank, isDefault: !hasBanks }],
      };
    });
  };

  const updateDefaultsBank = (bankId, field, value) => {
    setDefaultsForm((prev) => ({
      ...prev,
      banks: (prev.banks || []).map((bank) =>
        String(bank.id) === String(bankId)
          ? { ...bank, [field]: field === "ifsc" ? String(value || "").toUpperCase() : value }
          : bank
      ),
    }));
  };

  const removeDefaultsBank = (bankId) => {
    setDefaultsForm((prev) => {
      const nextBanks = (prev.banks || []).filter((bank) => String(bank.id) !== String(bankId));
      const nextDefaultBankId =
        String(prev.defaultBankId) === String(bankId)
          ? nextBanks[0]?.id || ""
          : prev.defaultBankId;
      return {
        ...prev,
        defaultBankId: nextDefaultBankId,
        banks: nextBanks.map((bank) => ({ ...bank, isDefault: String(bank.id) === String(nextDefaultBankId) })),
      };
    });
  };

  const submitDefaults = async (e) => {
    e.preventDefault();
    try {
      setDefaultsSaving(true);
      setErr("");
      const payload = {
        termsTitle: defaultsForm.termsTitle,
        notes: defaultsForm.notes,
        documentRemarks: defaultsForm.documentRemarks,
        paymentType: defaultsForm.paymentType,
        seriesByType: {
          sales: {
            invoiceSeries: defaultsForm?.seriesByType?.sales?.invoiceSeries || "TAX INVOICE",
            invoicePrefix: defaultsForm?.seriesByType?.sales?.invoicePrefix || "SAL",
            invoiceSuffix: defaultsForm?.seriesByType?.sales?.invoiceSuffix || "",
          },
          purchase: {
            invoiceSeries: defaultsForm?.seriesByType?.purchase?.invoiceSeries || "TAX INVOICE",
            invoicePrefix: defaultsForm?.seriesByType?.purchase?.invoicePrefix || "PUR",
            invoiceSuffix: defaultsForm?.seriesByType?.purchase?.invoiceSuffix || "",
          },
        },
        defaultBankId: defaultsForm.defaultBankId,
        banks: (defaultsForm.banks || []).map((bank) => ({
          id: bank.id,
          label: bank.label,
          bankName: bank.bankName,
          accountName: bank.accountName,
          accountNumber: bank.accountNumber,
          ifsc: bank.ifsc,
          branch: bank.branch,
          upiId: bank.upiId,
          isDefault: String(bank.id) === String(defaultsForm.defaultBankId || ""),
        })),
      };
      const res = await API.put("/employee/accounts/invoices/defaults", payload);
      const nextDefaults = {
        ...createInvoiceDefaults(),
        ...(res?.data?.invoiceDefaults || payload),
        seriesByType: {
          ...createInvoiceDefaults().seriesByType,
          ...(res?.data?.invoiceDefaults?.seriesByType || payload.seriesByType || {}),
          sales: {
            ...createInvoiceDefaults().seriesByType.sales,
            ...(res?.data?.invoiceDefaults?.seriesByType?.sales || payload.seriesByType.sales || {}),
          },
          purchase: {
            ...createInvoiceDefaults().seriesByType.purchase,
            ...(res?.data?.invoiceDefaults?.seriesByType?.purchase || payload.seriesByType.purchase || {}),
          },
        },
        banks: Array.isArray(res?.data?.invoiceDefaults?.banks || payload.banks)
          ? (res?.data?.invoiceDefaults?.banks || payload.banks).map((bank) => createBankEntry(bank))
          : [],
      };
      setInvoiceDefaults(nextDefaults);
      setDefaultsForm(nextDefaults);
      setForm((prev) => {
        if (prev.invoiceNo || prev.vendorId || (prev.items || []).some((item) => String(item.description || "").trim() || Number(item.qty || 0) > 1)) {
          return prev;
        }
        const seeded = createInvoiceForm(nextDefaults, activeInvoiceType);
        return { ...seeded, items: prev.items || [emptyItem()] };
      });
      setShowDefaultsModal(false);
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Invoice defaults save failed");
    } finally {
      setDefaultsSaving(false);
    }
  };

  const submitVendorCreate = async (e) => {
    e.preventDefault();
    try {
      setErr("");
      setVendorSubmitting(true);
      const res = await API.post("/employee/accounts/vendors", vendorForm);
      const createdVendor = res?.data?.vendor;
      setCreatedVendorPassword(res?.data?.tempPassword || "");
      await fetchVendors();
      if (createdVendor?._id) {
        setForm((prev) => ({ ...prev, vendorId: createdVendor._id }));
      }
      setShowVendorCreate(false);
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Vendor create failed");
    } finally {
      setVendorSubmitting(false);
    }
  };

  const formatDate = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (!Number.isFinite(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN");
  };

  const formatDateInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (!Number.isFinite(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const printInvoiceDocument = async (invoiceSource) => {
    try {
      await downloadAccountsInvoicePdf({ invoice: invoiceSource || {} });
    } catch (e) {
      setErr(e?.message || "Failed to generate invoice PDF");
    }
  };

  const downloadInvoicePdf = async () => {
    const { payload, error } = buildInvoicePayload();
    if (error) return setErr(error);
    await printInvoiceDocument({
      ...payload,
      vendor: payload.vendor,
      referenceNo: payload.referenceNo || "",
      dispatchDetails: {},
    });
  };

  const buildDetailForm = (invoice) => ({
    _id: invoice?._id || "",
    invoiceNo: invoice?.invoiceNo || "",
    invoiceSeries: invoice?.invoiceSeries || "",
    invoicePrefix: invoice?.invoicePrefix || "",
    invoiceSuffix: invoice?.invoiceSuffix || "",
    invoiceType: invoice?.invoiceType || "sales",
    invoiceDate: formatDateInput(invoice?.invoiceDate),
    dueDate: formatDateInput(invoice?.dueDate),
    referenceNo: invoice?.referenceNo || invoice?.dispatchDetails?.trackingId || "",
    notes: invoice?.notes || "",
    termsTitle: invoice?.termsTitle || "",
    documentRemarks: invoice?.documentRemarks || "",
    paymentType: invoice?.paymentType || "CREDIT",
    bankId: invoice?.bankDetails?.id || "",
    bankName: invoice?.bankName || invoice?.bankDetails?.bankName || "",
    bankDetails: invoice?.bankDetails || null,
    items: (invoice?.items || []).map((item) => ({
      productId: item?.productId || "",
      description: item?.description || "",
      unit: item?.unit || "",
      qty: item?.qty ?? 1,
      rate: item?.rate ?? 0,
      taxPercent: item?.taxPercent ?? 0,
    })),
    dispatchDetails: {
      ...(invoice?.dispatchDetails || {}),
      dispatchDate: formatDateInput(invoice?.dispatchDetails?.dispatchDate),
      expectedDeliveryDate: formatDateInput(invoice?.dispatchDetails?.expectedDeliveryDate),
      timeline: Array.isArray(invoice?.dispatchDetails?.timeline) ? invoice.dispatchDetails.timeline : [],
    },
  });

  const openInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailEditMode(false);
    setDetailForm(buildDetailForm(invoice));
  };

  const closeInvoiceDetail = () => {
    setSelectedInvoice(null);
    setDetailEditMode(false);
    setDetailForm(null);
  };

  const setDetailItemField = (index, field, value) => {
    setDetailForm((prev) => ({
      ...prev,
      items: (prev?.items || []).map((item, idx) => (idx === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addDetailItemRow = () => {
    setDetailForm((prev) => ({ ...prev, items: [...(prev?.items || []), emptyItem()] }));
  };

  const removeDetailItemRow = (index) => {
    setDetailForm((prev) => {
      const nextItems = (prev?.items || []).filter((_, idx) => idx !== index);
      return { ...prev, items: nextItems.length ? nextItems : [emptyItem()] };
    });
  };

  const submitDetailUpdate = async () => {
    if (!selectedInvoice?._id || !detailForm) return;
    const totals = calcTotals(detailForm.items || []);
    const validItems = totals.items.filter((i) => String(i.description || "").trim() && Number(i.qty) > 0);
    if (!String(detailForm.invoiceNo || "").trim()) return setErr("Invoice No is required");
    if (!validItems.length) return setErr("Add at least one valid item");
    try {
      setErr("");
      setDetailSubmitting(true);
      const payload = {
        invoiceNo: String(detailForm.invoiceNo || "").trim(),
        invoiceType: detailForm.invoiceType || selectedInvoice.invoiceType || "sales",
        invoiceSeries: detailForm.invoiceSeries || "",
        invoicePrefix: detailForm.invoicePrefix || "",
        invoiceSuffix: detailForm.invoiceSuffix || "",
        invoiceDate: detailForm.invoiceDate || undefined,
        dueDate: detailForm.dueDate || undefined,
        referenceNo: detailForm.referenceNo || "",
        notes: detailForm.notes || "",
        termsTitle: detailForm.termsTitle || "",
        documentRemarks: detailForm.documentRemarks || "",
        paymentType: detailForm.paymentType || "CREDIT",
        bankName: detailForm.bankDetails?.bankName || detailForm.bankName || "",
        bankDetails: detailForm.bankDetails || null,
        items: validItems,
        amount: totals.totalAmount,
        dispatchDetails: {
          ...(detailForm.dispatchDetails || {}),
          dispatchDate: detailForm.dispatchDetails?.dispatchDate || undefined,
          expectedDeliveryDate: detailForm.dispatchDetails?.expectedDeliveryDate || undefined,
        },
      };
      const res = await API.put(`/employee/accounts/invoices/${selectedInvoice._id}`, payload);
      const updated = res?.data || { ...selectedInvoice, ...payload };
      setSelectedInvoice(updated);
      setDetailForm(buildDetailForm(updated));
      setDetailEditMode(false);
      fetchRows();
    } catch (e) {
      setErr(e?.response?.data?.message || "Invoice update failed");
    } finally {
      setDetailSubmitting(false);
    }
  };

  const setItemField = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item, idx) => {
        if (idx !== index) return item;
        if (field === "description") {
          const typed = String(value || "");
          const exact = productOptions.find((product) => product.searchKey === typed.trim().toLowerCase());
          if (exact) return applyProductPricing({ ...item, description: typed }, exact);
          return { ...item, description: typed, productId: "" };
        }
        if (field === "qty") {
          const nextItem = { ...item, qty: value };
          const selectedProduct = productOptions.find((product) => String(product._id) === String(item.productId));
          return selectedProduct ? applyProductPricing(nextItem, selectedProduct, value) : nextItem;
        }
        return { ...item, [field]: value };
      }),
    }));
  };

  const getItemSuggestions = (item) => {
    const q = String(item?.description || "").trim().toLowerCase();
    if (q.length < 1 || hasExactSelectedProduct(item, productOptions)) return [];
    return productOptions.filter((product) => product.searchKey.includes(q)).slice(0, 8);
  };

  const selectProductForItem = (index, product) => {
    setForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item, idx) => (
        idx === index ? applyProductPricing(item, product) : item
      )),
    }));
  };

  const addItemRow = () => {
    setForm((prev) => ({ ...prev, items: [...(prev.items || []), emptyItem()] }));
  };

  const removeItemRow = (index) => {
    setForm((prev) => {
      const nextItems = (prev.items || []).filter((_, idx) => idx !== index);
      return { ...prev, items: nextItems.length ? nextItems : [emptyItem()] };
    });
  };

  const updateStatus = async (id, status) => {
    try {
      await API.patch(`/employee/accounts/invoices/${id}/status`, { status });
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Update failed");
    }
  };

  const removeInvoice = async (id) => {
    if (!id) return;
    const ok = window.confirm("Is invoice ko delete karna hai?");
    if (!ok) return;
    try {
      await API.delete(`/employee/accounts/invoices/${id}`);
      if (selectedInvoice?._id === id) closeInvoiceDetail();
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Delete failed");
    }
  };

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const status = String(r.status || "").toLowerCase();
      const type = String(r.invoiceType || "purchase").toLowerCase();
      if (type !== activeInvoiceType) return false;
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (!q) return true;
      const text = [
        r.invoiceNo,
        r.invoiceType,
        r.vendor?.name,
        r.vendor,
        r.amount,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(q);
    });
  }, [rows, query, statusFilter, activeInvoiceType]);

  const statusTone = (value) => {
    const v = String(value || "").toLowerCase();
    if (v === "paid") return "paid";
    if (v === "approved") return "approved";
    if (v === "pending") return "pending";
    return "default";
  };

  return (
    <div className="invoice-page">
      <header className="invoice-header">
        <div>
          <h2 className="invoice-title">Invoices</h2>
          <p className="invoice-subtitle">Create, review, and approve invoices for your organization.</p>
        </div>
        <div className="invoice-header-actions">
          {canCreate ? (
            <button className="invoice-create-btn invoice-defaults-btn" type="button" onClick={openDefaultsModal}>
              <span className="material-symbols-outlined">tune</span>
              Invoice Defaults
            </button>
          ) : null}
          {canCreate ? (
            <button className="invoice-create-btn" type="button" onClick={() => openCreate(activeInvoiceType)}>
              <span className="material-symbols-outlined">post_add</span>
              {activeInvoiceType === "sales" ? "Create Sales Invoice" : "Create Purchase Invoice"}
            </button>
          ) : null}
        </div>
      </header>

      <div className="invoice-type-tabs">
        {[
          { key: "sales", label: "Sales Invoices" },
          { key: "purchase", label: "Purchase Invoices" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`invoice-type-tab ${activeInvoiceType === tab.key ? "active" : ""}`}
            onClick={() => setActiveInvoiceType(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="invoice-toolbar">
        <label className="invoice-search">
          <span className="material-symbols-outlined">search</span>
          <input
            placeholder="Search by invoice number, vendor, or amount..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="invoice-filters">
          {["all", "pending", "approved", "paid"].map((s) => (
            <button
              key={s}
              type="button"
              className={`invoice-chip ${statusFilter === s ? "active" : ""} tone-${s}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === "all" ? "All Statuses" : s[0].toUpperCase() + s.slice(1)}
              <span className="material-symbols-outlined">expand_more</span>
            </button>
          ))}
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}
      {createdVendorPassword ? (
        <Alert variant="success">
          Vendor account created. Credentials have been prepared for the vendor. Temporary login password: <strong>{createdVendorPassword}</strong>. Vendor can now login and complete the remaining profile details.
        </Alert>
      ) : null}

      <div className="invoice-table-card">
        {loading ? (
          <div className="invoice-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="invoice-table-wrap">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th className="check-col">
                    <input type="checkbox" />
                  </th>
                  <th>Invoice No</th>
                  <th>Type</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created Date</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="invoice-empty">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, i) => (
                    <tr key={r._id || r.id || i}>
                      <td className="check-col">
                        <input type="checkbox" />
                      </td>
                      <td className="invoice-no">{orDash(r.invoiceNo)}</td>
                      <td>{String(r.invoiceType || "purchase").replace(/^./, (c) => c.toUpperCase())}</td>
                      <td className="invoice-vendor">{orDash(r.vendor?.name || r.vendor)}</td>
                      <td className="invoice-amount">₹ {orDash(r.totalAmount ?? r.amount)}</td>
                      <td>
                        <span className={`invoice-pill ${statusTone(r.status)}`}>
                          {orDash(r.status)}
                        </span>
                      </td>
                      <td className="invoice-date">{fmtDate(r.createdAt)}</td>
                      <td className="invoice-actions">
                        {r.canEditDelete || r.canApproveReject ? (
                          <>
                            <button
                              className="invoice-action view"
                              type="button"
                              onClick={() => openInvoiceDetail(r)}
                            >
                              View
                            </button>
                            {canCreate ? (
                              <button
                                className="invoice-action approve"
                                type="button"
                                onClick={() => {
                                  openInvoiceDetail(r);
                                  setDetailEditMode(true);
                                  setDetailForm(buildDetailForm(r));
                                }}
                              >
                                Edit
                              </button>
                            ) : null}
                            {canCreate ? (
                              <button
                                className="invoice-action reject"
                                type="button"
                                onClick={() => removeInvoice(r._id)}
                              >
                                Delete
                              </button>
                            ) : null}
                            <button
                              className="invoice-action approve"
                              type="button"
                              onClick={() => updateStatus(r._id, "approved")}
                              disabled={!canApprove || !r.canApproveReject}
                            >
                              Approve
                            </button>
                            <button
                              className="invoice-action reject"
                              type="button"
                              onClick={() => updateStatus(r._id, "rejected")}
                              disabled={!canApprove || !r.canApproveReject}
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <button className="invoice-action view" type="button" onClick={() => openInvoiceDetail(r)}>
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="invoice-pagination">
          <p>Showing 1 to {Math.min(filteredRows.length, 10)} of {filteredRows.length} results</p>
          <div className="invoice-pages">
            <button type="button" disabled>
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <button type="button" className="active">1</button>
            <button type="button">2</button>
            <button type="button">3</button>
            <button type="button">
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {showCreate ? (
        <form
          className="invoice-workspace-shell"
          onSubmit={(e) => {
            e.preventDefault();
            submitCreate("approve");
          }}
        >
          <div className="invoice-create-head">
            <div className="invoice-create-head-main">
              <div>
                <p className="invoice-create-kicker">Invoice Workspace</p>
                <h3 className="invoice-create-title">
                  {form.invoiceType === "sales" ? "Create Sale Invoice" : "Create Purchase Invoice"}
                </h3>
                <p className="invoice-create-sub">Customer info, invoice detail, product items aur summary ek hi workspace me manage karo.</p>
              </div>
              <div className="invoice-create-head-actions">
                <button type="button" className="invoice-create-another" onClick={() => openCreate(form.invoiceType)}>
                  <span className="material-symbols-outlined">add</span>
                  Create Another Invoice
                </button>
                <button type="button" className="invoice-workspace-close" onClick={() => setShowCreate(false)} aria-label="Close invoice workspace">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
          </div>
          <div className="invoice-create-body">
            <section className="invoice-builder-top">
              <article className="invoice-create-card invoice-builder-panel">
                <div className="invoice-builder-sectionhead">
                  <h4>Customer Information</h4>
                  <button type="button" className="invoice-panel-iconbtn" onClick={openVendorCreate}>
                    <span className="material-symbols-outlined">person_add</span>
                  </button>
                </div>
                <div className="invoice-builder-grid customer">
                  <label className="invoice-field invoice-field-wide">
                    <span>M/S.</span>
                    <select
                      value={form.vendorId}
                      onChange={(e) => setForm({ ...form, vendorId: e.target.value })}
                      required
                    >
                      <option value="">Select vendor</option>
                      {vendors.map((v) => (
                        <option key={v._id} value={v._id}>
                          {v.companyName || v.name || v.fullName}
                          {v.sourceType === "party" ? " [Party Ledger]" : " [Vendor]"}
                          {v.mobile ? ` (${v.mobile})` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="invoice-field invoice-field-wide">
                    <span>Address</span>
                    <textarea
                      rows={2}
                      value={selectedVendor?.address || ""}
                      placeholder="Vendor address will appear here"
                      readOnly
                    />
                  </label>
                  <label className="invoice-field">
                    <span>Contact Person</span>
                    <input value={selectedVendor?.contactName || selectedVendor?.fullName || ""} readOnly placeholder="Contact person" />
                  </label>
                  <label className="invoice-field">
                    <span>Phone No</span>
                    <input value={selectedVendor?.contactMobile || selectedVendor?.mobile || ""} readOnly placeholder="Phone number" />
                  </label>
                  <label className="invoice-field">
                    <span>GSTIN / PAN</span>
                    <input value={selectedVendor?.gstNumber || ""} readOnly placeholder="GSTIN / PAN" />
                  </label>
                  <label className="invoice-field">
                    <span>Rev. Charge</span>
                    <select value={form.reverseCharge} onChange={(e) => setForm({ ...form, reverseCharge: e.target.value })}>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </label>
                  <label className="invoice-field">
                    <span>Ship To</span>
                    <select value={form.shipTo} onChange={(e) => setForm({ ...form, shipTo: e.target.value })}>
                      <option value="--">--</option>
                      <option value="Billing Address">Billing Address</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="Dispatch Address">Dispatch Address</option>
                    </select>
                  </label>
                  <label className="invoice-field">
                    <span>Place of Supply</span>
                    <input
                      value={form.placeOfSupply}
                      onChange={(e) => setForm({ ...form, placeOfSupply: e.target.value })}
                      placeholder="State / place of supply"
                    />
                  </label>
                </div>
              </article>

              <article className="invoice-create-card invoice-builder-panel">
                <div className="invoice-builder-sectionhead">
                  <h4>{form.invoiceType === "sales" ? "Sales Invoice Detail" : "Purchase Invoice Detail"}</h4>
                  <button type="button" className="invoice-panel-iconbtn" onClick={downloadInvoicePdf}>
                    <span className="material-symbols-outlined">file_download</span>
                  </button>
                </div>
                <div className="invoice-builder-grid detail">
                  <label className="invoice-field">
                    <span>Invoice Type</span>
                    <input value={form.invoiceType === "sales" ? "Sales Invoice" : "Purchase Invoice"} readOnly />
                  </label>
                  <label className="invoice-field">
                    <span>Invoice Series</span>
                    <select value={form.invoiceSeries} onChange={(e) => setForm({ ...form, invoiceSeries: e.target.value })}>
                      <option value="TAX INVOICE">TAX INVOICE</option>
                      <option value="RETAIL INVOICE">RETAIL INVOICE</option>
                      <option value="EXPORT INVOICE">EXPORT INVOICE</option>
                    </select>
                  </label>
                  <label className="invoice-field invoice-field-wide">
                    <span>Invoice No.</span>
                    <div className="invoice-no-row">
                      <input value={form.invoicePrefix} onChange={(e) => setForm({ ...form, invoicePrefix: e.target.value })} placeholder="Inv Pre." />
                      <input
                        value={form.invoiceNo}
                        onChange={(e) => setForm({ ...form, invoiceNo: e.target.value })}
                        placeholder="Invoice number"
                        required
                      />
                      <input value={form.invoiceSuffix} onChange={(e) => setForm({ ...form, invoiceSuffix: e.target.value })} placeholder="Inv Pos" />
                    </div>
                  </label>
                  <label className="invoice-field">
                    <span>Date</span>
                    <input
                      type="date"
                      value={form.invoiceDate}
                      onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
                    />
                  </label>
                  <label className="invoice-field">
                    <span>Driver no.</span>
                    <input value={form.driverNo} onChange={(e) => setForm({ ...form, driverNo: e.target.value })} placeholder="Driver no." />
                  </label>
                  <label className="invoice-field">
                    <span>P.O. No.</span>
                    <input value={form.poNo} onChange={(e) => setForm({ ...form, poNo: e.target.value })} placeholder="P.O. No." />
                  </label>
                  <label className="invoice-field">
                    <span>P.O. Date</span>
                    <input type="date" value={form.poDate} onChange={(e) => setForm({ ...form, poDate: e.target.value })} />
                  </label>
                  <label className="invoice-field">
                    <span>L.R. No.</span>
                    <input value={form.lrNo} onChange={(e) => setForm({ ...form, lrNo: e.target.value })} placeholder="L.R. No." />
                  </label>
                  <label className="invoice-field">
                    <span>E-Way No.</span>
                    <input value={form.eWayNo} onChange={(e) => setForm({ ...form, eWayNo: e.target.value })} placeholder="E-Way No." />
                  </label>
                </div>
                <div className="invoice-builder-delivery">
                  <label className="invoice-field invoice-field-wide">
                    <span>Delivery</span>
                    <select value={form.deliveryMode} onChange={(e) => setForm({ ...form, deliveryMode: e.target.value })}>
                      <option value="">Select Delivery Mode</option>
                      <option value="Road Transport">Road Transport</option>
                      <option value="Pickup">Pickup</option>
                      <option value="Courier">Courier</option>
                      <option value="Hand Delivery">Hand Delivery</option>
                    </select>
                  </label>
                </div>
                {form.deliveryMode ? (
                  <div className="invoice-builder-grid delivery-extended">
                    <label className="invoice-field">
                      <span>Transporter Name</span>
                      <input
                        value={form.transporterName}
                        onChange={(e) => setForm({ ...form, transporterName: e.target.value })}
                        placeholder="Transporter / agency name"
                      />
                    </label>
                    <label className="invoice-field">
                      <span>Vehicle Number</span>
                      <input
                        value={form.vehicleNumber}
                        onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })}
                        placeholder="UP32 AB 1234"
                        required
                      />
                    </label>
                    <label className="invoice-field">
                      <span>Driver Name</span>
                      <input
                        value={form.driverName}
                        onChange={(e) => setForm({ ...form, driverName: e.target.value })}
                        placeholder="Driver name"
                        required
                      />
                    </label>
                    <label className="invoice-field">
                      <span>Driver Mobile</span>
                      <input
                        value={form.driverMobile}
                        onChange={(e) => setForm({ ...form, driverMobile: e.target.value })}
                        placeholder="Driver mobile number"
                        required
                      />
                    </label>
                    <label className="invoice-field">
                      <span>Dispatch Date</span>
                      <input
                        type="date"
                        value={form.dispatchDate}
                        onChange={(e) => setForm({ ...form, dispatchDate: e.target.value })}
                      />
                    </label>
                    <label className="invoice-field">
                      <span>Expected Delivery Date</span>
                      <input
                        type="date"
                        value={form.expectedDeliveryDate}
                        onChange={(e) => setForm({ ...form, expectedDeliveryDate: e.target.value })}
                      />
                    </label>
                    <label className="invoice-field invoice-field-wide">
                      <span>Delivery Address</span>
                      <textarea
                        rows={2}
                        value={form.deliveryAddress}
                        onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
                        placeholder="Exact delivery destination"
                        required
                      />
                    </label>
                    <label className="invoice-field invoice-field-wide">
                      <span>Current Location / Handover Point</span>
                      <textarea
                        rows={2}
                        value={form.currentLocation}
                        onChange={(e) => setForm({ ...form, currentLocation: e.target.value })}
                        placeholder="Pickup point / current location"
                      />
                    </label>
                    <label className="invoice-field invoice-field-wide">
                      <span>Delivery Instructions</span>
                      <textarea
                        rows={3}
                        value={form.deliveryInstructions}
                        onChange={(e) => setForm({ ...form, deliveryInstructions: e.target.value })}
                        placeholder="Gate entry, unloading, contact person, timing, special notes"
                      />
                    </label>
                  </div>
                ) : null}
              </article>
            </section>

            <section className="invoice-create-card invoice-builder-items">
              <div className="invoice-items-head invoice-items-head-strong">
                <h4>Product Items</h4>
                <div className="invoice-items-inline-actions">
                  <label className="invoice-inline-mini">
                    <span>Discount</span>
                    <input value={createDiscountAmount.toFixed(2)} readOnly />
                  </label>
                  <button type="button" className="invoice-add-item-btn" onClick={addItemRow}>
                    <span className="material-symbols-outlined">add_circle</span>
                    Add Line Item
                  </button>
                </div>
              </div>
              <div className="invoice-items-wrap invoice-items-wrap-builder">
                <table className="invoice-items-table invoice-items-table-builder">
                  <thead>
                    <tr>
                      <th>Sr.</th>
                      <th>Product / Other Charges</th>
                      <th>HSN / SAC</th>
                      <th>Qty.</th>
                      <th>UOM</th>
                      <th>Price (₹)</th>
                      <th>Discount</th>
                      <th>IGST</th>
                      <th>Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(form.items || []).map((item, idx) => {
                      const qty = Number(item.qty || 0);
                      const rate = Number(item.rate || 0);
                      const discount = Number(item.discount || 0);
                      const taxPercent = Number(item.taxPercent || 0);
                      const lineSubtotal = Math.max((qty * rate) - discount, 0);
                      const lineTax = (lineSubtotal * taxPercent) / 100;
                      const lineTotal = lineSubtotal + lineTax;
                      return (
                        <tr key={`item-${idx}`}>
                          <td className="invoice-sr-col">{idx + 1}</td>
                          <td>
                            <div className="invoice-product-field">
                              <input
                                placeholder="Enter product name"
                                value={item.description}
                                onChange={(e) => setItemField(idx, "description", e.target.value)}
                                list={`invoice-product-list-${idx}`}
                                autoComplete="off"
                                required
                              />
                              <textarea
                                rows={2}
                                placeholder="Item note..."
                                value={item.note || ""}
                                onChange={(e) => setItemField(idx, "note", e.target.value)}
                              />
                              <datalist id={`invoice-product-list-${idx}`}>
                                {getItemSuggestions(item).map((product) => (
                                  <option key={product._id} value={product.name}>
                                    {product.unit ? `${product.name} (${product.unit})` : product.name}
                                  </option>
                                ))}
                              </datalist>
                              {getItemSuggestions(item).length ? (
                                <div className="invoice-product-suggestions">
                                  {getItemSuggestions(item).map((product) => {
                                    const tier = findRateTier(product, item.qty);
                                    return (
                                      <button
                                        key={product._id}
                                        type="button"
                                        className="invoice-product-option"
                                        onClick={() => selectProductForItem(idx, product)}
                                      >
                                        <span>{product.name}</span>
                                        <small>
                                          ₹ {Number(tier?.rate || 0).toFixed(2)}
                                          {product.unit ? ` / ${product.unit}` : ""}
                                        </small>
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td><input value={item.hsnSac || ""} onChange={(e) => setItemField(idx, "hsnSac", e.target.value)} placeholder="HSN/SAC" /></td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.qty}
                              onChange={(e) => setItemField(idx, "qty", e.target.value)}
                              required
                            />
                          </td>
                          <td><input value={item.unit || ""} onChange={(e) => setItemField(idx, "unit", e.target.value)} placeholder="UOM" /></td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => setItemField(idx, "rate", e.target.value)}
                              readOnly={form.invoiceType === "purchase" && !!item.productId}
                              required
                            />
                          </td>
                          <td><input value={item.discount || "0"} onChange={(e) => setItemField(idx, "discount", e.target.value)} placeholder="0" /></td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.taxPercent}
                              onChange={(e) => setItemField(idx, "taxPercent", e.target.value)}
                              readOnly={form.invoiceType === "purchase" && !!item.productId}
                              placeholder="0"
                            />
                          </td>
                          <td className="text-end line-total">₹ {lineTotal.toFixed(2)}</td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="invoice-delete-item-btn"
                              onClick={() => removeItemRow(idx)}
                              disabled={(form.items || []).length <= 1}
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="8" className="invoice-total-label">Total Inv. Val</td>
                      <td className="invoice-total-value">₹ {createGrandTotal.toFixed(2)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>

            <section className="invoice-builder-bottom">
              <article className="invoice-create-card invoice-builder-panel">
                <div className="invoice-builder-grid ancillary">
                  <label className="invoice-field">
                    <span>Due Date</span>
                    <input
                      type="date"
                      value={form.dueDate}
                      onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                    />
                  </label>
                  <label className="invoice-field">
                    <span>Bank</span>
                    <select
                      value={form.bankId}
                      onChange={(e) => {
                        const nextBank = (invoiceDefaults?.banks || []).find((bank) => String(bank.id) === String(e.target.value));
                        setForm({
                          ...form,
                          bankId: e.target.value,
                          bankName: nextBank?.bankName || "",
                        });
                      }}
                    >
                      <option value="">Select bank</option>
                      {(invoiceDefaults?.banks || []).map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.label || bank.bankName}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {selectedBank ? (
                  <div className="invoice-bank-preview">
                    <strong>{selectedBank.label || selectedBank.bankName}</strong>
                    <span>{selectedBank.bankName || "Bank name not set"}</span>
                    <span>{selectedBank.accountNumber ? `A/C ${selectedBank.accountNumber}` : "Account number not set"}</span>
                    <span>{selectedBank.ifsc ? `IFSC ${selectedBank.ifsc}` : "IFSC not set"}</span>
                  </div>
                ) : null}

                <div className="invoice-builder-sectionhead compact">
                  <h4>Terms & Condition / Additional Note</h4>
                </div>
                <div className="invoice-builder-grid notes">
                  <label className="invoice-field">
                    <span>Title</span>
                    <input value={form.termsTitle} onChange={(e) => setForm({ ...form, termsTitle: e.target.value })} />
                  </label>
                  <label className="invoice-field invoice-field-wide">
                    <span>Detail</span>
                    <textarea
                      rows={5}
                      placeholder="Subject to our home jurisdiction..."
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </label>
                  <label className="invoice-field invoice-field-wide">
                    <span>Document Note / Remarks</span>
                    <textarea
                      rows={3}
                      placeholder="Not visible on print"
                      value={form.documentRemarks}
                      onChange={(e) => setForm({ ...form, documentRemarks: e.target.value })}
                    />
                  </label>
                </div>
              </article>

              <article className="invoice-create-card invoice-total-card invoice-builder-summary">
                <h4>Summary</h4>
                <div className="invoice-total-row">
                  <span>Taxable</span>
                  <strong>₹ {createSubtotal.toFixed(2)}</strong>
                </div>
                <div className="invoice-total-row">
                  <span>Add Additional Charge</span>
                  <strong>₹ {createAdditionalCharge.toFixed(2)}</strong>
                </div>
                <div className="invoice-total-row">
                  <span>Total Tax</span>
                  <strong>₹ {createTaxAmount.toFixed(2)}</strong>
                </div>
                <div className="invoice-total-row">
                  <span>Discount</span>
                  <strong>₹ {createDiscountAmount.toFixed(2)}</strong>
                </div>
                <div className="invoice-total-row grand">
                  <span>Grand Total</span>
                  <strong>₹ {createGrandTotal.toFixed(2)}</strong>
                </div>
                <div className="invoice-summary-words">
                  <span>Total in words</span>
                  <strong>{createGrandTotal <= 0 ? "ZERO RUPEES ONLY" : "Amount auto calculated from invoice items"}</strong>
                </div>
                <div className="invoice-paymenttype">
                  <span>Payment Type</span>
                  <div className="invoice-paymenttype-list">
                    {PAYMENT_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={form.paymentType === type ? "active" : ""}
                        onClick={() => setForm({ ...form, paymentType: type })}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            </section>
          </div>
          <div className="invoice-create-foot">
            <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              disabled={submitting}
              onClick={() => submitCreate("draft")}
            >
              {submitting ? "Saving..." : "Save Invoice"}
            </Button>
            <Button type="button" variant="outline-dark" onClick={downloadInvoicePdf} disabled={submitting}>
              Download PDF
            </Button>
            {canApprove ? (
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? "Processing..." : "Approve & Send"}
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}

      <Modal
        show={!!selectedInvoice}
        onHide={closeInvoiceDetail}
        fullscreen
        scrollable
        dialogClassName="invoice-create-modal invoice-detail-fullscreen"
      >
        <Modal.Header closeButton className="invoice-create-head">
          <div>
            <p className="invoice-create-kicker">Invoice Detail</p>
            <Modal.Title>{selectedInvoice?.invoiceNo || "Invoice"}</Modal.Title>
            <p className="invoice-create-sub">
              {selectedInvoice?.autoGenerated ? "Auto-generated from dispatch workflow." : "Invoice detail overview."}
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="invoice-create-body">
          <section className="invoice-create-card">
            <h4>Overview</h4>
            <div className="invoice-create-grid">
              <label className="invoice-field">
                <span>Vendor</span>
                <input value={selectedInvoice?.vendor || ""} readOnly />
              </label>
              <label className="invoice-field">
                <span>Invoice Type</span>
                {detailEditMode ? (
                  <select
                    value={detailForm?.invoiceType || ""}
                    onChange={(e) => setDetailForm((prev) => ({ ...prev, invoiceType: e.target.value }))}
                  >
                    <option value="purchase">Purchase Invoice</option>
                    <option value="sales">Sales Invoice</option>
                  </select>
                ) : (
                  <input value={selectedInvoice?.invoiceType || ""} readOnly />
                )}
              </label>
              <label className="invoice-field">
                <span>Invoice Date</span>
                {detailEditMode ? (
                  <input
                    type="date"
                    value={detailForm?.invoiceDate || ""}
                    onChange={(e) => setDetailForm((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                  />
                ) : (
                  <input value={formatDate(selectedInvoice?.invoiceDate)} readOnly />
                )}
              </label>
              <label className="invoice-field">
                <span>Due Date</span>
                {detailEditMode ? (
                  <input
                    type="date"
                    value={detailForm?.dueDate || ""}
                    onChange={(e) => setDetailForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                  />
                ) : (
                  <input value={formatDate(selectedInvoice?.dueDate)} readOnly />
                )}
              </label>
              <label className="invoice-field">
                <span>Reference No</span>
                {detailEditMode ? (
                  <input
                    value={detailForm?.referenceNo || ""}
                    onChange={(e) => setDetailForm((prev) => ({ ...prev, referenceNo: e.target.value }))}
                  />
                ) : (
                  <input value={selectedInvoice?.referenceNo || selectedInvoice?.dispatchDetails?.trackingId || ""} readOnly />
                )}
              </label>
              <label className="invoice-field">
                <span>Total Amount</span>
                <input
                  value={`₹ ${Number((detailEditMode ? calcTotals(detailForm?.items || []).totalAmount : (selectedInvoice?.totalAmount || selectedInvoice?.amount || 0)) || 0).toFixed(2)}`}
                  readOnly
                />
              </label>
            </div>
          </section>

          {selectedInvoice?.dispatchDetails ? (
            <section className="invoice-create-card">
              <h4>Dispatch Details</h4>
              <div className="invoice-create-grid">
                <label className="invoice-field">
                  <span>Dispatch Status</span>
                  {detailEditMode ? (
                    <input
                      value={detailForm?.dispatchDetails?.dispatchStatus || ""}
                      onChange={(e) => setDetailForm((prev) => ({
                        ...prev,
                        dispatchDetails: { ...(prev?.dispatchDetails || {}), dispatchStatus: e.target.value },
                      }))}
                    />
                  ) : (
                    <input value={selectedInvoice.dispatchDetails.dispatchStatus || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Tracking ID</span>
                  {detailEditMode ? (
                    <input
                      value={detailForm?.dispatchDetails?.trackingId || ""}
                      onChange={(e) => setDetailForm((prev) => ({
                        ...prev,
                        dispatchDetails: { ...(prev?.dispatchDetails || {}), trackingId: e.target.value },
                      }))}
                    />
                  ) : (
                    <input value={selectedInvoice.dispatchDetails.trackingId || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Transporter</span>
                  {detailEditMode ? (
                    <input
                      value={detailForm?.dispatchDetails?.transporterName || ""}
                      onChange={(e) => setDetailForm((prev) => ({
                        ...prev,
                        dispatchDetails: { ...(prev?.dispatchDetails || {}), transporterName: e.target.value },
                      }))}
                    />
                  ) : (
                    <input value={selectedInvoice.dispatchDetails.transporterName || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Vehicle Number</span>
                  {detailEditMode ? (
                    <input
                      value={detailForm?.dispatchDetails?.vehicleNumber || ""}
                      onChange={(e) => setDetailForm((prev) => ({
                        ...prev,
                        dispatchDetails: { ...(prev?.dispatchDetails || {}), vehicleNumber: e.target.value },
                      }))}
                    />
                  ) : (
                    <input value={selectedInvoice.dispatchDetails.vehicleNumber || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Driver</span>
                  {detailEditMode ? (
                    <input
                      value={detailForm?.dispatchDetails?.driverName || ""}
                      onChange={(e) => setDetailForm((prev) => ({
                        ...prev,
                        dispatchDetails: { ...(prev?.dispatchDetails || {}), driverName: e.target.value },
                      }))}
                    />
                  ) : (
                    <input value={selectedInvoice.dispatchDetails.driverName || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Driver Mobile</span>
                  {detailEditMode ? (
                    <input
                      value={detailForm?.dispatchDetails?.driverMobile || ""}
                      onChange={(e) => setDetailForm((prev) => ({
                        ...prev,
                        dispatchDetails: { ...(prev?.dispatchDetails || {}), driverMobile: e.target.value },
                      }))}
                    />
                  ) : (
                    <input value={selectedInvoice.dispatchDetails.driverMobile || ""} readOnly />
                  )}
                </label>
              </div>
              <label className="invoice-field">
                <span>Delivery Location</span>
                {detailEditMode ? (
                  <textarea
                    rows={2}
                    value={detailForm?.dispatchDetails?.locationAddress || detailForm?.dispatchDetails?.currentLocation || ""}
                    onChange={(e) => setDetailForm((prev) => ({
                      ...prev,
                      dispatchDetails: {
                        ...(prev?.dispatchDetails || {}),
                        locationAddress: e.target.value,
                        currentLocation: e.target.value,
                      },
                    }))}
                  />
                ) : (
                  <textarea rows={2} value={selectedInvoice.dispatchDetails.locationAddress || selectedInvoice.dispatchDetails.currentLocation || ""} readOnly />
                )}
              </label>
              {(selectedInvoice.dispatchDetails.timeline || []).length ? (
                <div className="invoice-items-wrap">
                  <table className="invoice-items-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Location</th>
                        <th>Note</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedInvoice.dispatchDetails.timeline || []).map((event, index) => (
                        <tr key={`timeline-${index}`}>
                          <td>{orDash(event.status)}</td>
                          <td>{orDash(event.location)}</td>
                          <td>{orDash(event.note)}</td>
                          <td>{fmtDate(event.at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          ) : null}

          <section className="invoice-create-card">
            <h4>Items</h4>
            <div className="invoice-items-wrap">
              <table className="invoice-items-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Rate</th>
                    <th>Tax %</th>
                    <th className="text-end">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailEditMode ? detailForm?.items || [] : selectedInvoice?.items || []).map((item, index) => (
                    <tr key={`view-item-${index}`}>
                      <td>
                        {detailEditMode ? (
                          <input value={item.description || ""} onChange={(e) => setDetailItemField(index, "description", e.target.value)} />
                        ) : (
                          orDash(item.description)
                        )}
                      </td>
                      <td>
                        {detailEditMode ? (
                          <input type="number" min="0" step="0.01" value={item.qty} onChange={(e) => setDetailItemField(index, "qty", e.target.value)} />
                        ) : (
                          orDash(item.qty)
                        )}
                      </td>
                      <td>
                        {detailEditMode ? (
                          <input value={item.unit || ""} onChange={(e) => setDetailItemField(index, "unit", e.target.value)} />
                        ) : (
                          orDash(item.unit)
                        )}
                      </td>
                      <td>
                        {detailEditMode ? (
                          <input type="number" min="0" step="0.01" value={item.rate} onChange={(e) => setDetailItemField(index, "rate", e.target.value)} />
                        ) : (
                          <>₹ {orDash(item.rate)}</>
                        )}
                      </td>
                      <td>
                        {detailEditMode ? (
                          <input type="number" min="0" step="0.01" value={item.taxPercent} onChange={(e) => setDetailItemField(index, "taxPercent", e.target.value)} />
                        ) : (
                          orDash(item.taxPercent)
                        )}
                      </td>
                      <td className="text-end">
                        ₹ {Number(detailEditMode ? ((Number(item.qty || 0) * Number(item.rate || 0)) + ((Number(item.qty || 0) * Number(item.rate || 0) * Number(item.taxPercent || 0)) / 100)) : item.lineTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {detailEditMode ? (
              <div className="invoice-items-head" style={{ marginTop: 12 }}>
                <button type="button" className="invoice-add-item-btn" onClick={addDetailItemRow}>
                  <span className="material-symbols-outlined">add_circle</span>
                  Add Line Item
                </button>
                <button
                  type="button"
                  className="invoice-add-item-btn"
                  onClick={() => removeDetailItemRow((detailForm?.items || []).length - 1)}
                  disabled={(detailForm?.items || []).length <= 1}
                >
                  <span className="material-symbols-outlined">remove_circle</span>
                  Remove Last Item
                </button>
              </div>
            ) : null}
          </section>

          {selectedInvoice?.notes || selectedInvoice?.termsTitle || selectedInvoice?.bankName || detailEditMode ? (
            <section className="invoice-create-card">
              <h4>Invoice Defaults Snapshot</h4>
              <div className="invoice-create-grid">
                <label className="invoice-field">
                  <span>Terms Title</span>
                  {detailEditMode ? (
                    <input value={detailForm?.termsTitle || ""} onChange={(e) => setDetailForm((prev) => ({ ...prev, termsTitle: e.target.value }))} />
                  ) : (
                    <input value={selectedInvoice.termsTitle || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Payment Type</span>
                  {detailEditMode ? (
                    <select
                      value={detailForm?.paymentType || "CREDIT"}
                      onChange={(e) => setDetailForm((prev) => ({ ...prev, paymentType: e.target.value }))}
                    >
                      {PAYMENT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input value={selectedInvoice.paymentType || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field">
                  <span>Bank</span>
                  {detailEditMode ? (
                    <select
                      value={detailForm?.bankId || ""}
                      onChange={(e) => {
                        const nextBank = (invoiceDefaults?.banks || []).find((bank) => String(bank.id) === String(e.target.value));
                        setDetailForm((prev) => ({
                          ...prev,
                          bankId: e.target.value,
                          bankName: nextBank?.bankName || "",
                          bankDetails: nextBank || null,
                        }));
                      }}
                    >
                      <option value="">Select bank</option>
                      {(invoiceDefaults?.banks || []).map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.label || bank.bankName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input value={selectedInvoice.bankName || selectedInvoice.bankDetails?.bankName || ""} readOnly />
                  )}
                </label>
                <label className="invoice-field invoice-field-wide">
                  <span>Terms Detail</span>
                  {detailEditMode ? (
                    <textarea rows={4} value={detailForm?.notes || ""} onChange={(e) => setDetailForm((prev) => ({ ...prev, notes: e.target.value }))} />
                  ) : (
                    <textarea rows={4} value={selectedInvoice.notes} readOnly />
                  )}
                </label>
                <label className="invoice-field invoice-field-wide">
                  <span>Document Remarks</span>
                  {detailEditMode ? (
                    <textarea rows={3} value={detailForm?.documentRemarks || ""} onChange={(e) => setDetailForm((prev) => ({ ...prev, documentRemarks: e.target.value }))} />
                  ) : (
                    <textarea rows={3} value={selectedInvoice.documentRemarks || ""} readOnly />
                  )}
                </label>
              </div>
            </section>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="invoice-create-foot">
          <Button variant="outline-dark" type="button" onClick={() => printInvoiceDocument(detailEditMode && detailForm ? { ...selectedInvoice, ...detailForm, items: calcTotals(detailForm.items || []).items, dispatchDetails: detailForm.dispatchDetails } : selectedInvoice)}>
            Download PDF
          </Button>
          {detailEditMode ? (
            <>
              <Button variant="outline-secondary" onClick={() => { setDetailEditMode(false); setDetailForm(buildDetailForm(selectedInvoice)); }}>
                Cancel Edit
              </Button>
              <Button variant="primary" onClick={submitDetailUpdate} disabled={detailSubmitting}>
                {detailSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={closeInvoiceDetail}>
                Close
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDefaultsModal}
        onHide={() => setShowDefaultsModal(false)}
        fullscreen
        scrollable
        dialogClassName="invoice-create-modal invoice-defaults-modal"
      >
        <form onSubmit={submitDefaults}>
          <Modal.Header closeButton className="invoice-create-head">
            <div>
              <p className="invoice-create-kicker">Invoice Workspace</p>
              <Modal.Title>Invoice Defaults</Modal.Title>
              <p className="invoice-create-sub">Terms, remarks, payment type aur bank accounts yahin se default set honge.</p>
            </div>
          </Modal.Header>
          <Modal.Body className="invoice-create-body">
            <section className="invoice-create-card">
              <h4>Default Terms & Notes</h4>
              <div className="invoice-create-grid">
                <label className="invoice-field invoice-field-wide">
                  <span>Sales Series</span>
                  <div className="invoice-no-row">
                    <input
                      value={defaultsForm?.seriesByType?.sales?.invoicePrefix || ""}
                      onChange={(e) =>
                        setDefaultsForm((prev) => ({
                          ...prev,
                          seriesByType: {
                            ...(prev.seriesByType || {}),
                            sales: { ...(prev.seriesByType?.sales || {}), invoicePrefix: e.target.value },
                          },
                        }))
                      }
                      placeholder="SAL"
                    />
                    <select
                      value={defaultsForm?.seriesByType?.sales?.invoiceSeries || "TAX INVOICE"}
                      onChange={(e) =>
                        setDefaultsForm((prev) => ({
                          ...prev,
                          seriesByType: {
                            ...(prev.seriesByType || {}),
                            sales: { ...(prev.seriesByType?.sales || {}), invoiceSeries: e.target.value },
                          },
                        }))
                      }
                    >
                      <option value="TAX INVOICE">TAX INVOICE</option>
                      <option value="RETAIL INVOICE">RETAIL INVOICE</option>
                      <option value="EXPORT INVOICE">EXPORT INVOICE</option>
                    </select>
                    <input
                      value={defaultsForm?.seriesByType?.sales?.invoiceSuffix || ""}
                      onChange={(e) =>
                        setDefaultsForm((prev) => ({
                          ...prev,
                          seriesByType: {
                            ...(prev.seriesByType || {}),
                            sales: { ...(prev.seriesByType?.sales || {}), invoiceSuffix: e.target.value },
                          },
                        }))
                      }
                      placeholder="Suffix"
                    />
                  </div>
                </label>
                <label className="invoice-field invoice-field-wide">
                  <span>Purchase Series</span>
                  <div className="invoice-no-row">
                    <input
                      value={defaultsForm?.seriesByType?.purchase?.invoicePrefix || ""}
                      onChange={(e) =>
                        setDefaultsForm((prev) => ({
                          ...prev,
                          seriesByType: {
                            ...(prev.seriesByType || {}),
                            purchase: { ...(prev.seriesByType?.purchase || {}), invoicePrefix: e.target.value },
                          },
                        }))
                      }
                      placeholder="PUR"
                    />
                    <select
                      value={defaultsForm?.seriesByType?.purchase?.invoiceSeries || "TAX INVOICE"}
                      onChange={(e) =>
                        setDefaultsForm((prev) => ({
                          ...prev,
                          seriesByType: {
                            ...(prev.seriesByType || {}),
                            purchase: { ...(prev.seriesByType?.purchase || {}), invoiceSeries: e.target.value },
                          },
                        }))
                      }
                    >
                      <option value="TAX INVOICE">TAX INVOICE</option>
                      <option value="RETAIL INVOICE">RETAIL INVOICE</option>
                      <option value="EXPORT INVOICE">EXPORT INVOICE</option>
                    </select>
                    <input
                      value={defaultsForm?.seriesByType?.purchase?.invoiceSuffix || ""}
                      onChange={(e) =>
                        setDefaultsForm((prev) => ({
                          ...prev,
                          seriesByType: {
                            ...(prev.seriesByType || {}),
                            purchase: { ...(prev.seriesByType?.purchase || {}), invoiceSuffix: e.target.value },
                          },
                        }))
                      }
                      placeholder="Suffix"
                    />
                  </div>
                </label>
                <label className="invoice-field">
                  <span>Terms Title</span>
                  <input
                    value={defaultsForm.termsTitle}
                    onChange={(e) => setDefaultsForm((prev) => ({ ...prev, termsTitle: e.target.value }))}
                  />
                </label>
                <label className="invoice-field">
                  <span>Payment Type</span>
                  <select
                    value={defaultsForm.paymentType}
                    onChange={(e) => setDefaultsForm((prev) => ({ ...prev, paymentType: e.target.value }))}
                  >
                    {PAYMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="invoice-field invoice-field-wide">
                  <span>Terms Detail</span>
                  <textarea
                    rows={4}
                    value={defaultsForm.notes}
                    onChange={(e) => setDefaultsForm((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Subject to jurisdiction..."
                  />
                </label>
                <label className="invoice-field invoice-field-wide">
                  <span>Document Remarks</span>
                  <textarea
                    rows={3}
                    value={defaultsForm.documentRemarks}
                    onChange={(e) => setDefaultsForm((prev) => ({ ...prev, documentRemarks: e.target.value }))}
                    placeholder="Internal default remarks"
                  />
                </label>
              </div>
            </section>

            <section className="invoice-create-card">
              <div className="invoice-builder-sectionhead">
                <h4>Default Banks</h4>
                <button type="button" className="invoice-panel-iconbtn" onClick={addDefaultsBank}>
                  <span className="material-symbols-outlined">add</span>
                </button>
              </div>
              {!defaultsForm.banks?.length ? (
                <div className="invoice-inline-note">Abhi koi bank configured nahi hai. Naya bank add karke default select karo.</div>
              ) : null}
              <div className="invoice-defaults-banklist">
                {(defaultsForm.banks || []).map((bank) => (
                  <article key={bank.id} className={`invoice-default-bankcard ${String(defaultsForm.defaultBankId) === String(bank.id) ? "is-default" : ""}`}>
                    <div className="invoice-default-bankcard-head">
                      <label className="invoice-default-radio">
                        <input
                          type="radio"
                          name="defaultBankId"
                          checked={String(defaultsForm.defaultBankId) === String(bank.id)}
                          onChange={() => setDefaultBankId(bank.id)}
                        />
                        <span>Default Bank</span>
                      </label>
                      <button type="button" className="invoice-default-remove" onClick={() => removeDefaultsBank(bank.id)}>
                        Remove
                      </button>
                    </div>
                    <div className="invoice-create-grid">
                      <label className="invoice-field">
                        <span>Label</span>
                        <input value={bank.label} onChange={(e) => updateDefaultsBank(bank.id, "label", e.target.value)} placeholder="Primary Current A/C" />
                      </label>
                      <label className="invoice-field">
                        <span>Bank Name</span>
                        <input value={bank.bankName} onChange={(e) => updateDefaultsBank(bank.id, "bankName", e.target.value)} placeholder="STATE BANK OF INDIA" />
                      </label>
                      <label className="invoice-field">
                        <span>Account Name</span>
                        <input value={bank.accountName} onChange={(e) => updateDefaultsBank(bank.id, "accountName", e.target.value)} />
                      </label>
                      <label className="invoice-field">
                        <span>Account Number</span>
                        <input value={bank.accountNumber} onChange={(e) => updateDefaultsBank(bank.id, "accountNumber", e.target.value)} />
                      </label>
                      <label className="invoice-field">
                        <span>IFSC</span>
                        <input value={bank.ifsc} onChange={(e) => updateDefaultsBank(bank.id, "ifsc", e.target.value)} />
                      </label>
                      <label className="invoice-field">
                        <span>Branch</span>
                        <input value={bank.branch} onChange={(e) => updateDefaultsBank(bank.id, "branch", e.target.value)} />
                      </label>
                      <label className="invoice-field invoice-field-wide">
                        <span>UPI ID</span>
                        <input value={bank.upiId} onChange={(e) => updateDefaultsBank(bank.id, "upiId", e.target.value)} />
                      </label>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </Modal.Body>
          <Modal.Footer className="invoice-create-foot">
            <Button variant="outline-secondary" onClick={() => setShowDefaultsModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={defaultsSaving || defaultsLoading}>
              {defaultsSaving ? "Saving..." : "Save Defaults"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {showVendorCreate ? (
        <div className="invoice-side-overlay" role="presentation">
          <button
            type="button"
            className="invoice-side-backdrop"
            aria-label="Close add vendor panel"
            onClick={() => setShowVendorCreate(false)}
          />
          <aside className="invoice-side-panel" aria-label="Add vendor over invoice">
            <form onSubmit={submitVendorCreate} className="invoice-side-form">
              <div className="invoice-create-head invoice-side-head">
                <div>
                  <p className="invoice-create-kicker">Vendor Workspace</p>
                  <h3 className="invoice-create-title">Add Vendor</h3>
                  <p className="invoice-create-sub">Invoice band kiye bina yahan vendor add karo.</p>
                </div>
                <button type="button" className="invoice-workspace-close" onClick={() => setShowVendorCreate(false)} aria-label="Close">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="invoice-create-body invoice-side-body">
            <section className="invoice-create-card">
              <h4>Basic Details</h4>
              <div className="invoice-create-grid">
                <label className="invoice-field">
                  <span>Company Name</span>
                  <input value={vendorForm.companyName} onChange={(e) => setVendorForm({ ...vendorForm, companyName: e.target.value })} required />
                </label>
                <label className="invoice-field">
                  <span>Contact Name</span>
                  <input value={vendorForm.contactName} onChange={(e) => setVendorForm({ ...vendorForm, contactName: e.target.value })} required />
                </label>
                <label className="invoice-field">
                  <span>Mobile</span>
                  <input value={vendorForm.mobile} onChange={(e) => setVendorForm({ ...vendorForm, mobile: e.target.value })} required />
                </label>
                <label className="invoice-field">
                  <span>Email</span>
                  <input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} />
                </label>
                <label className="invoice-field">
                  <span>GST Number</span>
                  <div className="invoice-gst-autofill-row">
                    <input value={vendorForm.gstNumber} onChange={(e) => setVendorForm({ ...vendorForm, gstNumber: e.target.value.toUpperCase() })} required />
                    <button type="button" onClick={runVendorGstLookup} disabled={vendorLookupLoading}>
                      {vendorLookupLoading ? "Checking..." : "Auto Fill"}
                    </button>
                  </div>
                </label>
                <label className="invoice-field">
                  <span>Vendor Type</span>
                  <select value={vendorForm.vendorType} onChange={(e) => setVendorForm({ ...vendorForm, vendorType: e.target.value })}>
                    <option value="Distributor">Distributor</option>
                    <option value="Retailer">Retailer</option>
                    <option value="Super Stockist">Super Stockist</option>
                    <option value="Trader">Trader</option>
                  </select>
                </label>
                <label className="invoice-field">
                  <span>Contact Designation</span>
                  <input value={vendorForm.contactDesignation} onChange={(e) => setVendorForm({ ...vendorForm, contactDesignation: e.target.value })} />
                </label>
                <label className="invoice-field">
                  <span>WhatsApp Number</span>
                  <input value={vendorForm.whatsappNumber} onChange={(e) => setVendorForm({ ...vendorForm, whatsappNumber: e.target.value })} />
                </label>
                <label className="invoice-field">
                  <span>PAN Number</span>
                  <input value={vendorForm.panNumber} onChange={(e) => setVendorForm({ ...vendorForm, panNumber: e.target.value.toUpperCase() })} />
                </label>
              <label className="invoice-field">
                <span>Turnover</span>
                <input value={vendorForm.turnover} onChange={(e) => setVendorForm({ ...vendorForm, turnover: e.target.value })} placeholder="e.g. 2 Cr yearly" />
              </label>
              <label className="invoice-field invoice-field-wide">
                <span>Address Line 1</span>
                <input
                  value={vendorForm.addressLine1}
                  onChange={(e) => setVendorForm({ ...vendorForm, addressLine1: e.target.value })}
                  placeholder="House no, street, area"
                  required
                />
              </label>
              <label className="invoice-field invoice-field-wide">
                <span>Address Line 2</span>
                <input
                  value={vendorForm.addressLine2}
                  onChange={(e) => setVendorForm({ ...vendorForm, addressLine2: e.target.value })}
                  placeholder="Landmark, locality, industrial area"
                />
              </label>
              <label className="invoice-field">
                <span>City</span>
                <input value={vendorForm.city} onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })} required />
              </label>
              <label className="invoice-field">
                <span>State</span>
                <input value={vendorForm.state} onChange={(e) => setVendorForm({ ...vendorForm, state: e.target.value })} required />
              </label>
              <label className="invoice-field">
                <span>Pincode</span>
                <input
                  value={vendorForm.pincode}
                  onChange={(e) => setVendorForm({ ...vendorForm, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  placeholder="6 digit pincode"
                  required
                />
              </label>
            </div>
            {vendorPincodeMeta.loading ? (
              <div className="invoice-inline-note">Pincode se city/state fetch ho raha hai...</div>
            ) : null}
            {vendorPincodeMeta.error ? (
              <div className="invoice-inline-note invoice-inline-note-error">{vendorPincodeMeta.error}</div>
            ) : null}
            {!vendorPincodeMeta.loading && !vendorPincodeMeta.error && vendorPincodeMeta.localities.length ? (
              <div className="invoice-inline-note">
                Locality suggestion: {vendorPincodeMeta.localities.slice(0, 4).join(", ")}
              </div>
            ) : null}
            {vendorLookupMessage ? (
              <div className="invoice-inline-note">{vendorLookupMessage}</div>
            ) : null}
              <label className="invoice-field" style={{ marginTop: 12 }}>
                <span>Internal Notes</span>
                <textarea rows={3} value={vendorForm.notes} onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })} />
              </label>
            </section>
              </div>
              <div className="invoice-create-foot invoice-side-foot">
                <Button variant="outline-secondary" onClick={() => setShowVendorCreate(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={vendorSubmitting}>
                  {vendorSubmitting ? "Saving..." : "Save Vendor"}
                </Button>
              </div>
            </form>
          </aside>
        </div>
      ) : null}
    </div>
  );
};
